import type { AxiosResponse } from 'axios';
import { apiClient } from './api-client';
import { recoverySessionHeaders, writeRecoverySessionId } from '@/lib/recovery-session';

export interface RequestOtpResponse {
  message: string;
  maskedEmail: string;
  /** Constant for every caller — the client derives its own countdown from it. */
  cooldownSeconds: number;
  otpExpiresAt: string;
  /**
   * D-143 — browser-scoped session counter. `sessionRequestsUsed` counts requests
   * made from THIS browser, not the account's OTP usage: the account figure would
   * differ between a real and an unknown address and so may never be returned to
   * an anonymous caller. `sessionRequestLimit` is the policy value 5.
   */
  recoverySessionId: string;
  sessionRequestsUsed: number;
  sessionRequestLimit: number;
}

export interface VerifyOtpResponse {
  resetToken: string;
  expiresAt: string;
}

export const passwordResetService = {
  /**
   * `secondaryIdentifier` is sent only after a 409
   * ADDITIONAL_IDENTIFICATION_REQUIRED, i.e. the address is shared by several
   * active accounts and the server needs to know which one is asking.
   */
  requestOtp: (email: string, secondaryIdentifier?: string): Promise<RequestOtpResponse> =>
    apiClient
      .post<RequestOtpResponse>(
        '/auth/password-reset/request',
        {
          email,
          ...(secondaryIdentifier ? { secondaryIdentifier } : {}),
        },
        // D-143 — present the stored session so the server continues this
        // browser's count instead of minting a new one.
        { headers: recoverySessionHeaders() },
      )
      .then((r: AxiosResponse<RequestOtpResponse>) => {
        // Persist whatever the server issued: on a first request, or after the
        // session expired at UTC midnight, this is a brand-new id.
        writeRecoverySessionId(r.data?.recoverySessionId);
        return r.data;
      }),

  verifyOtp: (email: string, otp: string): Promise<VerifyOtpResponse> =>
    apiClient
      .post<VerifyOtpResponse>('/auth/password-reset/verify-otp', { email, otp })
      .then((r: AxiosResponse<VerifyOtpResponse>) => r.data),

  resetPassword: (
    resetToken: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<void> =>
    apiClient
      .post<void>('/auth/password-reset/reset', { resetToken, newPassword, confirmPassword })
      .then(() => undefined),
};
